import { CORRUPTION_CHARS_JA, CORRUPTION_CHARS_EN } from '@darkchat/shared'

/**
 * 腐敗レベルに応じて挿入率を取得
 */
export function getCorruptionInsertRate(corruptionLevel: number): number {
  if (corruptionLevel === 0) {
    return 0
  }

  // 腐敗レベルに応じて挿入率を調整
  if (corruptionLevel < 20) {
    return 0.10
  } else if (corruptionLevel < 40) {
    return 0.15
  } else if (corruptionLevel < 60) {
    return 0.25
  } else if (corruptionLevel < 80) {
    return 0.35
  } else if (corruptionLevel < 95) {
    return 0.45
  } else {
    return 0.55
  }
}

/**
 * 腐敗レベルに応じて段階的にテキストを腐敗させる設定を取得（後方互換性用）
 * @deprecated getCorruptionInsertRateを使用してください
 */
export function getCorruptionConfig(corruptionLevel: number, text: string = '') {
  const insertRate = getCorruptionInsertRate(corruptionLevel)

  // テキスト全体から言語を判定（後方互換性のため）
  const isJapanese = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/.test(text)
  const chars = isJapanese ? CORRUPTION_CHARS_JA : CORRUPTION_CHARS_EN

  return {
    insertRate,
    chars
  }
}

/**
 * 設定に基づいてテキストを腐敗させる（日本語：挿入のみ、英語：挿入＋大文字小文字反転）
 * 各文字の種類（全角/半角）に応じて適切な腐敗文字を挿入
 * @param text 元のテキスト
 * @param corruptionLevel 腐敗レベル (0-100)
 */
export function applyCorruption(text: string, corruptionLevel: number): string {
  const insertRate = getCorruptionInsertRate(corruptionLevel)
  if (insertRate === 0) {
    return text
  }

  const chars = text.split('')
  const result: string[] = []

  for (let i = 0; i < chars.length; i++) {
    let char = chars[i]!

    // 空白文字は腐敗させない
    if (char === ' ' || char === '\n' || char === '\t') {
      result.push(char)
      continue
    }

    // 現在の文字が日本語（全角）かどうかを判定
    // ひらがな、カタカナ、漢字、全角記号（句読点など）を含む
    const isCharJapanese = /[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF\u3000-\u303F\uFF00-\uFFEF]/.test(char)

    // 英語（半角）文字の場合、大文字小文字を反転させる可能性
    if (!isCharJapanese && /[a-zA-Z]/.test(char)) {
      // 腐敗レベルに応じて反転確率を決定（0-30%）
      const flipRate = corruptionLevel * 0.003
      if (Math.random() < flipRate) {
        char = char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
      }
    }

    // 処理後の文字を追加
    result.push(char)

    // 挿入判定（文字の後に腐敗文字を挿入、最大3回連続）
    // 英語文字の場合は挿入率を50%に減らす（読みやすさのため）
    const effectiveInsertRate = isCharJapanese ? insertRate : insertRate * 0.5
    if (Math.random() < effectiveInsertRate) {
      // 現在の文字の種類に応じて腐敗文字セットを選択
      const charCorruptionChars = isCharJapanese ? CORRUPTION_CHARS_JA : CORRUPTION_CHARS_EN

      if (charCorruptionChars.length > 0) {
        // 挿入する腐敗文字を1つ選択（連続挿入時も同じ文字を使用）
        const corruptChar = charCorruptionChars[Math.floor(Math.random() * charCorruptionChars.length)]!

        // 連続挿入回数を決定（1〜3回）
        // 腐敗レベルが高いほど連続しやすい
        const maxRepeat = corruptionLevel >= 80 ? 3 : corruptionLevel >= 50 ? 2 : 1
        let repeatCount = 1

        // 腐敗レベルに応じて連続確率を調整
        if (maxRepeat > 1 && Math.random() < corruptionLevel / 100) {
          repeatCount = Math.floor(Math.random() * maxRepeat) + 1
        }

        // 同じ腐敗文字を連続で挿入
        for (let j = 0; j < repeatCount; j++) {
          result.push(corruptChar)
        }
      }
    }
  }

  return result.join('')
}
